import type { ReactElement } from 'react';
import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { CategoryStatisticQuestion } from 'types/category-statistic';
import './question-identifier.scss';

type Question = Pick<
  CategoryStatisticQuestion,
  'id' | 'identifier' | 'isOmitted' | 'isCorrect' | 'questionType'
>;

// ESLint thinks these are unused props when used with D3:
type QuestionIdentifier = {
  // eslint-disable-next-line react/no-unused-prop-types
  question: Question & { className?: string };
  radius?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  fontSize?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  onClickQuestion?: (id: number) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  canHaveStyledQuestionType?: boolean;
};

/**
 * Build string of conditional classnames to apply
 */
const getQuestionClasses = (question: Question): string =>
  [
    !question.identifier ? 'invisible' : null,
    question.isOmitted ? 'omitted' : null,
    question.isCorrect ? 'correct' : 'incorrect',
    question.questionType?.className
  ].join(' ');

export function QuestionIdentifierNode(props: QuestionIdentifier): Element | null {
  const { question, radius = 4, fontSize = 12, onClickQuestion, canHaveStyledQuestionType } = props;

  const questionIdentifier = d3
    .create('svg:g')
    .classed('question-identifier', true)
    .classed('can-have-type', canHaveStyledQuestionType)
    .classed(question?.className, !!question?.className)
    .classed(getQuestionClasses(question), true)
    .classed('clickable', !!onClickQuestion)
    .attr('x', -radius)
    .attr('y', -radius)
    .on('click', () => onClickQuestion && onClickQuestion(question.id));

  questionIdentifier.append('circle').attr('class', 'question-circle').attr('r', radius);

  questionIdentifier
    .append('text')
    .text(question.identifier)
    .attr('class', 'question-number')
    .attr('text-anchor', 'middle')
    .attr('font-size', fontSize)
    .attr('dy', fontSize / 2 - 1);

  return questionIdentifier.node();
}

export function QuestionIdentifierSVG(props: QuestionIdentifier): ReactElement | null {
  const { radius = 4 } = props;
  const svgRef = useRef<HTMLDivElement>();
  // Plot chart after JSX rendered
  useEffect(() => {
    const container = svgRef.current;
    if (!container) {
      return undefined;
    }
    d3.select(container)
      .append('svg:svg')
      .attr('class', 'question-svg')
      .attr('viewBox', `${-radius} ${-radius} ${radius * 2} ${radius * 2}`)
      .attr('width', radius * 2)
      .attr('height', radius * 2)
      .append(() => QuestionIdentifierNode({ ...props }));

    return () => {
      d3.select(container).selectAll('*').remove();
    };
  }, [props, radius]);

  return <div className="question-svg-holder" ref={svgRef} />;
}
